import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to fetch admin statistics
 * Handles caching, error states, and loading
 */
export function useAdminStats(endpoint, days = 30) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!endpoint) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const url = `${baseUrl}/api${endpoint}?days=${days}`;

      // Get token from localStorage like other API calls
      const token = localStorage.getItem('qdn_access_token');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError('Admin access required');
        } else {
          setError(`Error: ${response.status}`);
        }
        setData(null);
      } else {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error(`Error fetching admin stats from ${endpoint}:`, err);
      setError(err.message || 'Failed to load statistics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint, days]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refetch = useCallback(() => {
    fetchStats();
  }, [fetchStats]);

  return { data, loading, error, refetch };
}
